import React, { useEffect, useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { Loader2, TrendingUp, Receipt, Store as StoreIcon, Trophy, ArrowLeft, RefreshCw } from "lucide-react";

export const AdminAnalytics = () => {
    const { user, loading: authLoading } = useAuth();
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    const fetchData = async () => {
        setLoading(true);
        try {
            const { data: d } = await api.get("/admin/analytics");
            setData(d);
        } catch (err) {
            // not admin or error
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (authLoading) return;
        if (!user || user.role !== "admin") { navigate("/admin"); return; }
        fetchData();
    }, [user, authLoading, navigate]);

    if (authLoading || loading) {
        return (
            <div className="cyber-bg min-h-screen grid place-items-center">
                <Loader2 className="animate-spin text-[var(--gold-light)]" size={28} />
            </div>
        );
    }
    if (!data) {
        return (
            <div className="cyber-bg min-h-screen grid place-items-center text-white/60">
                Χωρίς δεδομένα
            </div>
        );
    }

    const t = data.totals;

    return (
        <div className="cyber-bg min-h-screen text-white pb-12" data-testid="admin-analytics">
            <header className="sticky top-0 z-30 glass-gold border-b border-[var(--gold)]/30 px-4 py-3 flex items-center gap-3">
                <button onClick={() => navigate("/admin")} className="h-10 w-10 grid place-items-center rounded-full hover:bg-white/10">
                    <ArrowLeft size={16} />
                </button>
                <div className="flex-1">
                    <p className="font-mono text-[9px] uppercase tracking-[0.25em] text-[var(--gold-light)]">// admin · analytics</p>
                    <h1 className="font-heading text-xl leading-none" style={{ fontFamily: '"Cormorant Garamond", serif' }}>Επισκόπηση Πλατφόρμας</h1>
                </div>
                <button onClick={fetchData} data-testid="analytics-refresh" className="h-10 w-10 grid place-items-center rounded-full hover:bg-white/10">
                    <RefreshCw size={14} />
                </button>
            </header>

            <div className="px-4 py-5 max-w-5xl mx-auto space-y-5">
                {/* Top KPIs */}
                <section className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <KPI icon={TrendingUp} label="Έσοδα" value={`€${t.revenue.toFixed(2)}`} accent="#39ff8f" testid="kpi-revenue" />
                    <KPI icon={Receipt}    label="Παραγγελίες" value={t.orders} accent="#f4b339" testid="kpi-orders" />
                    <KPI icon={Trophy}     label="Προμήθεια 10%" value={`€${t.commission.toFixed(2)}`} accent="#ff9f1c" testid="kpi-commission" />
                    <KPI icon={StoreIcon}  label="Καταστήματα" value={t.stores} accent="#00f0ff" testid="kpi-stores" />
                </section>

                {/* By store */}
                <section className="glass-gold rounded-2xl p-4">
                    <h2 className="font-heading text-lg mb-3" style={{ fontFamily: '"Cormorant Garamond", serif' }}>Κατάταξη Καταστημάτων</h2>
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="text-left text-[10px] font-mono uppercase tracking-widest text-white/40 border-b border-white/10">
                                    <th className="py-2 pr-2">#</th>
                                    <th className="py-2 pr-2">Κατάστημα</th>
                                    <th className="py-2 pr-2 text-right">Παραγγελίες</th>
                                    <th className="py-2 pr-2 text-right">Έσοδα</th>
                                    <th className="py-2 pr-2 text-right">Μ.Ο.</th>
                                    <th className="py-2 text-right">Προμήθεια</th>
                                </tr>
                            </thead>
                            <tbody>
                                {data.by_store.map((s, i) => (
                                    <tr key={s.store_id} data-testid={`store-row-${s.store_slug}`} className="border-b border-white/5 hover:bg-white/3">
                                        <td className="py-3 text-white/40 font-mono">{i + 1}</td>
                                        <td className="py-3 pr-2">
                                            <p className="font-bold">{s.store_name}</p>
                                            <p className="text-[10px] text-white/40 capitalize">{s.cuisine}</p>
                                        </td>
                                        <td className="py-3 pr-2 text-right font-mono">{s.orders}</td>
                                        <td className="py-3 pr-2 text-right font-mono font-bold text-[var(--gold-light)]">€{s.revenue.toFixed(2)}</td>
                                        <td className="py-3 pr-2 text-right font-mono text-white/60">€{s.avg_order.toFixed(2)}</td>
                                        <td className="py-3 text-right font-mono text-emerald-400">€{s.commission.toFixed(2)}</td>
                                    </tr>
                                ))}
                                {data.by_store.length === 0 && (
                                    <tr><td colSpan={6} className="py-6 text-center text-white/40 italic">Καμία παραγγελία ακόμα</td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </section>

                {/* Top items */}
                <section className="glass-gold rounded-2xl p-4">
                    <h2 className="font-heading text-lg mb-3" style={{ fontFamily: '"Cormorant Garamond", serif' }}>Best Sellers</h2>
                    <div className="space-y-2">
                        {data.top_items.length === 0 ? (
                            <p className="text-white/40 text-sm italic text-center py-4">—</p>
                        ) : data.top_items.map((it, i) => (
                            <div key={i} className="flex items-center gap-3 p-2 rounded-lg hover:bg-white/5" data-testid={`top-item-${i}`}>
                                <span className="font-mono text-sm font-bold text-[var(--gold-light)] w-7">#{i + 1}</span>
                                <span className="flex-1 text-sm font-bold truncate">{it.name}</span>
                                <span className="font-mono text-sm text-white/60">{it.qty} τμχ</span>
                                <span className="font-mono text-sm text-[var(--gold-light)] w-20 text-right">€{it.revenue.toFixed(2)}</span>
                            </div>
                        ))}
                    </div>
                </section>

                {/* Status distribution */}
                <section className="glass-gold rounded-2xl p-4">
                    <h2 className="font-heading text-lg mb-3" style={{ fontFamily: '"Cormorant Garamond", serif' }}>Κατάσταση Παραγγελιών</h2>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                        {Object.entries(data.status_distribution).map(([status, count]) => (
                            <div key={status} className="bg-white/5 rounded-lg p-3 text-center" data-testid={`status-${status}`}>
                                <p className="font-mono text-[10px] uppercase tracking-widest text-white/50">{status}</p>
                                <p className="font-heading text-2xl font-black mt-1 text-[var(--gold-light)]">{count}</p>
                            </div>
                        ))}
                    </div>
                </section>
            </div>
        </div>
    );
};

const KPI = ({ icon: Icon, label, value, accent, testid }) => (
    <article data-testid={testid} className="glass-gold rounded-2xl p-4 relative overflow-hidden">
        <div className="absolute -top-6 -right-6 h-20 w-20 rounded-full" style={{ background: `radial-gradient(circle, ${accent}33, transparent)` }} />
        <Icon size={18} className="mb-2" style={{ color: accent }} />
        <p className="text-[10px] font-mono uppercase tracking-widest text-white/55">{label}</p>
        <p className="font-heading text-2xl font-black mt-0.5" style={{ color: accent, fontFamily: '"Kanit", sans-serif' }}>{value}</p>
    </article>
);
